import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { t } from "i18next";
import { useParams, useHistory } from "react-router-dom";
// service hooks
import useGetStoreInfo from "service-hooks/useGetStoreInfo";
import useGetSavedAddressList from "service-hooks/useGetSavedAddressList";
import useAddItemToBasket from "components/item/hooks/useAddItemToBasket";
import useOpenSavedAddressListModal from "service-hooks/useOpenSavedAddressListModal";
import {
  getDiscount,
  getDiscountedPrice,
  getBasketValue as getDisplayValue,
} from "utils/discounts.utils";
import { NotPersistAction, ItemAction } from "store/actions";
import useCustomHistory from "hooks/useCustomHistory";
import C from "Constants";
// components
import { BrandHeader } from "components";
import ModiferGroups from "components/Modifier/ModiferGroups";
import ItemDetailModal from "components/item/ItemDetailModal";
import { navConfigKeys } from "components/brandHeader/UserNavPanel";
import StoreAndLocationModals from "components/Modals/StoreAndLocationModals";
import { TranslationText } from "components/TextTranslationContainer";
import ConditionalRenderer from "components/ConditionalRenderer";
import { RenderDiscountedPrice } from "components/DisplayPrice";
import { QuantitySelector } from "components/item/Quantity";
import WithLoading from "components/Hoc/WithLoading";
import { PriceRender } from "components/DisplayPrice";
import { CustomButton } from "components/Button";
import AddressList from "features/delivery-address/modals/AddressList";
import MenuDisabledModal from "../components/MenuDisabledModal";
// hooks
import useIsStoreSelected from "../../hooks/useIsStoreSelected";
import useUpdateUtmMedium from "../../../components/item/hooks/useUpdateUtmMedium";
import useDeliveryPickupFn from "features/hooks/useDelivertPickupFn";
import useDineIn from "modules/dine-in/hooks/useDineIn";

const STORE_PAGE_URL = "/store/store-detail";

const Footer = ({
  selectedItem,
  getBasketValue,
  addSelectedToBasket,
  errorMessage,
  singleItem,
  decreaseQuantity,
  increaseQuantity,
  modalLoading,
  isDineIn,
  params,
  isModal,
  discount,
}) => {
  const dispatch = useDispatch();

  const isMaxStockReached =
    selectedItem?.itemInfo?.isStockEnabled &&
    singleItem?.quantity + 1 > selectedItem?.itemInfo?.availableStock;

  let isDisabled =
    isMaxStockReached || +getBasketValue() <= 0 || errorMessage;

  // isDisabled merges several unrelated reasons, so the footer used to print
  // "select at least one modifier" for all of them — including on an item with
  // no modifiers that had simply run out of stock.
  const isOutOfStock =
    selectedItem?.isOutOfStock ||
    (selectedItem?.itemInfo?.isStockEnabled &&
      !selectedItem?.itemInfo?.availableStock);

  const blockingMessage =
    errorMessage ||
    (isOutOfStock
      ? t("This item is out of stock, sorry for inconvenience")
      : isMaxStockReached
      ? t("Maximum available quantity reached")
      : t("Please select at least one modifier"));

  const { orderType } = useSelector((state) => state.AppReducer);
  const { selectedStore, outletBasedSelectedStoreArea } = useSelector(
    (state) => state.store
  );
  const { openAddressListModal } = useOpenSavedAddressListModal();

  const isNotStoreSelected = isDineIn
    ? false
    : !selectedStore ||
      (orderType?.serviceType === C?.SERVICE_TYPE?.DELIVERY &&
        !outletBasedSelectedStoreArea);

  return (
    <div className="bg-dark-blue-100 w-full p-4 shadow-top">
      <ConditionalRenderer condition={isDisabled && !modalLoading}>
        <div className="flex justify-between items-stretch gap-4">
          <div className="text-red-500 mb-2">
            {blockingMessage}
          </div>
        </div>
      </ConditionalRenderer>

      <ConditionalRenderer condition={discount?.discountInfoMessage}>
        <p className="text-red-500 text-14x mb-2">
          {t(discount?.discountInfoMessage?.message, {
            discount: discount?.discountInfoMessage?.discount,
            price: discount?.discountInfoMessage?.price,
            symbol: discount?.discountInfoMessage?.symbol,
          })}
        </p>
      </ConditionalRenderer>

      <ConditionalRenderer condition={discount?.discountedPrice}>
        <div className="mb-2 flex justify-end items-center mx-4">
          <RenderDiscountedPrice
            price={{
              ...discount,
              discountedPrice: discount?.discountedPrice * singleItem?.quantity,
              actualPrice: discount?.actualPrice * singleItem?.quantity,
            }}
          />
        </div>
      </ConditionalRenderer>

      <div className="flex justify-between items-stretch gap-4">
        <QuantitySelector
          {...{ decreaseQuantity, increaseQuantity }}
          value={singleItem?.quantity}
          disable={
            +getBasketValue() <= 0 ||
            !!errorMessage ||
            modalLoading ||
            (!isDineIn && isNotStoreSelected)
          }
          disableIncrease={isMaxStockReached}
          disableMessage={t("Maximum available quantity reached")}
        />
        <CustomButton
          onClick={() => {
            if (!isNotStoreSelected) {
              addSelectedToBasket();
            } else {
              isModal && dispatch(NotPersistAction.setIsToggledFromModal(true));
              openAddressListModal(
                isModal
                  ? {
                      type: "modal",
                      ...params,
                    }
                  : null
              );
            }
          }}
          className={`flex items-center ${
            !isNotStoreSelected ? "justify-between" : "justify-center"
          }`}
          disabisModalled={isDisabled || modalLoading}
        >
          <TranslationText
            className="text-white"
            text={
              !isNotStoreSelected
                ? "Add to basket"
                : orderType?.serviceType === C?.SERVICE_TYPE?.DELIVERY
                ? "Select your location"
                : "Select store"
            }
          />
          {!isNotStoreSelected && (
            <PriceRender
              price={+getBasketValue()}
              className="text-16x leading-none text-white"
              currencySymbolClassName="text-white"
            />
          )}
        </CustomButton>
      </div>
    </div>
  );
};

const ItemModalContainer = ({
  params: propParams,
  handleClose = () => {},
  open,
  isDineIn,
  triggerCloseModal = false,
  ...rest
}) => {
  const dispatch = useDispatch();
  // redux selectors
  const { menuParams } = useSelector((state) => state.store);
  const { modalLoading, isLoading } = useSelector((state) => state.loader);
  const { DomainType, orderType } = useSelector((state) => state.AppReducer);
  const { discounts } = useSelector((state) => state?.brand);
  const { selectedStore, outletBasedSelectedStoreArea } = useSelector(
    (state) => state.store
  );
  const { goToHome: goToHomeDineIn } = useDineIn();
  const {goToHome: goToHomeDelivery} = useDeliveryPickupFn();

  const routeParams = useParams();
  const history = useHistory();
  const params = propParams ?? routeParams;

  const { brandInfo } = useSelector((state) => state?.brandStorageReducer);
  const { selectedItem, error } = useSelector((state) => state?.item);
  // hooks
  const router = useCustomHistory();
  const getStoreInfoWithMenu = useGetStoreInfo();
  let isStoreSelected = useIsStoreSelected();
  const { addItemToBasket } = useAddItemToBasket();

  if (isDineIn) {
    isStoreSelected = () => true;
  }

  useUpdateUtmMedium({ isModal: true, params });
  useGetSavedAddressList({
    fetch: true,
    type: "filtered",
  });
  // state
  const [discountPrice, setDiscountPrice] = useState(null);
  const [itemSection, setItemSection] = useState([]);
  const [singleItem, setSingleItem] = useState({
    quantity: 1,
    itemSection: [],
    price: selectedItem?.itemInfo?.price || 0,
  });
  const [errorMessage, setErrorMessage] = useState(null);

  // functions
  const showErrorMessage = (message) => {
    setErrorMessage(message);
    setTimeout(() => {
      setErrorMessage(null);
    }, 4000);
  };

  const updateLocalState = (key, value) => {
    setSingleItem((prevSingleItem) => ({
      ...prevSingleItem,
      [key]: value,
    }));
  };

  const decreaseQuantity = () => {
    setErrorMessage(null);
    if (singleItem?.quantity > 1) {
      updateLocalState("quantity", singleItem.quantity - 1);
    }
  };

  const increaseQuantity = () => {
    if (
      selectedItem?.itemInfo?.isStockEnabled &&
      singleItem?.quantity + 1 > selectedItem?.availableStock
    ) {
      const message = t("is the maximum quantity allowed for", {
        quantity: selectedItem?.availableStock,
        name: selectedItem?.itemInfo?.name,
      });
      setErrorMessage(message);
      showErrorMessage(message);
      return;
    }
    setErrorMessage(null);
    updateLocalState("quantity", singleItem.quantity + 1);
  };

  const applicableDiscount = useMemo(() => {
    return getDiscount({
      discounts,
      item: selectedItem?.itemInfo,
      storeId: selectedStore?._id,
      orderType: orderType?.serviceType,
    });
  }, [discounts, selectedItem?.itemInfo]);

  const handleResetReduxState = async () => {
    await dispatch(NotPersistAction.toggleAreaModal(false));
    await dispatch(NotPersistAction.setIsToggledFromModal(false));
  };

  const handleModalClose = async () => {
    await handleResetReduxState();
    if (triggerCloseModal) {
      handleClose();
    } else if (
      !isDineIn &&
      selectedStore &&
      ((orderType?.serviceType === C.SERVICE_TYPE.DELIVERY &&
        outletBasedSelectedStoreArea) ||
        orderType?.serviceType === C.SERVICE_TYPE.PICKUP)
    ) {
      router(
        STORE_PAGE_URL,
        `/${params.domainName}${STORE_PAGE_URL}`,
        DomainType,
        { fromItemScreen: true }
      );
    } else if (propParams?.type === "modal") {
      handleClose();
    } else {
      if(window.history.length > 1){
        history.goBack();
      }else{
        if(orderType?.serviceType === C.SERVICE_TYPE.DINE_IN){
          goToHomeDineIn();
        }else{
          goToHomeDelivery();
        }
      }
    }
  };

  const addSelectedToBasket = () => {
    addItemToBasket(
      brandInfo,
      selectedItem,
      singleItem,
      itemSection,
      handleModalClose,
      setErrorMessage,
      discountPrice
    );
  };

  const updateMenuActiveStatus = () => {
    dispatch(ItemAction.setMenuActiveStatus(false));
    getStoreInfoWithMenu(
      menuParams.storeId,
      menuParams.areaID,
      menuParams.storeArea
    );
  };

  const getLoading = () => {
    if (propParams?.type?.toLowerCase() === "modal") {
      return modalLoading;
    }
    return isLoading || false;
  };

  const isNotStoreSelected = isDineIn
    ? false
    : !selectedStore ||
      (orderType?.serviceType === C?.SERVICE_TYPE?.DELIVERY &&
        !outletBasedSelectedStoreArea);

  const getBasketValue = () => {
    return getDisplayValue({
      price: singleItem?.price,
      quantity: singleItem?.quantity,
      discountPrice: discountPrice,
    });
  };

  // mounting state handlers
  useEffect(() => {
    updateLocalState("price", selectedItem?.itemInfo?.price || 0);
    return () => {
      dispatch(NotPersistAction.setRequiredMissingSections([]));
    };
  }, [selectedItem?.itemInfo?.price]);

  useEffect(() => {
    if (singleItem?.price && applicableDiscount) {
      const discount = getDiscountedPrice({
        itemPrice: singleItem?.price,
        quantity: singleItem?.quantity,
        applicableDiscount: {
          ...applicableDiscount,
        },
      });
      if (discount?.discountedPrice || discount?.discountInfoMessage) {
        setDiscountPrice(discount);
      }
    }
  }, [singleItem?.price, applicableDiscount, singleItem?.quantity]);

  // prevent/ user to use the same discount twice,
  // useEffect(() => {
  //   if (applicableDiscount && !discountInfo && discountPrice) {
  //     // console.log("applicableDiscount", applicableDiscount);
  //     // dispatch(DiscountAction.setDiscountCode(applicableDiscount));
  //   }
  // }, [applicableDiscount, discountInfo, discountPrice]);

  return (
    <WithLoading loading={getLoading()}>
      <div className="flex w-full flex-col store-page-popup ">
        {!propParams && (
          <BrandHeader
            page={true}
            hideUserNavPanel={false}
            userNavPanelProps={{
              navIconConfig: [navConfigKeys.search, navConfigKeys.cart],
              searchTerm: null,
              authClick: () => {},
              userClick: () => {},
              handleSearch: () => {},
              cartClick: () => {},
              showBackButton: true,
            }}
          />
        )}
        <ItemDetailModal
          handleClose={handleModalClose}
          open={propParams?.type?.toLowerCase() === "modal" ? open : true}
          isLoading={getLoading()}
          selectedItem={selectedItem}
          hideOutsideCloseButton={propParams ? false : true}
          params={params}
          error={error}
          footerComponent={
            getLoading() ? null : (
              <Footer
                {...{
                  selectedItem,
                  getBasketValue,
                  addSelectedToBasket,
                  isDineIn,
                  errorMessage,
                  singleItem,
                  decreaseQuantity,
                  increaseQuantity,
                  modalLoading,
                  params,
                }}
                discount={discountPrice}
                isModal={propParams?.type?.toLowerCase() === "modal"}
              />
            )
          }
        >
          <ModiferGroups
            isNewUI={true}
            isStoreSelected={isStoreSelected()}
            disable={getLoading() || isNotStoreSelected}
            {...{
              selectedItem,
              setItemSection,
              itemSection,
              singleItem,
              setSingleItem,
            }}
          />
        </ItemDetailModal>
        <ConditionalRenderer condition={!isDineIn}>
          <StoreAndLocationModals />
          <MenuDisabledModal handleRefresh={updateMenuActiveStatus} />
          <AddressList
            page={`/store/item/${params?.itemId}/${params?.catId}`}
            pageFrom="item-detail"
          />
        </ConditionalRenderer>
      </div>
    </WithLoading>
  );
};

export default ItemModalContainer;
